import axios from 'axios'
import { useBoolean } from '@rothzerg/useboolean'
import { isEmail, isEmpty } from '@rothzerg/validator'
import React, { useState } from 'react'
import { createUseStyles } from 'react-jss'
import FormInput from '../ui/Form/FormInput'
import FormSelect from '../ui/Form/FormSelect'
import FormTextArea from '../ui/Form/FormTextArea'
import SVG from '../ui/SVG/SVG'

const Contactform = ({ body, tollfree, nottollfree }) => {
  const [formSubmitting, setFormSubmitting] = useBoolean()
  const [email, setEmail] = useState('')
  const [fname, setFname] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [enqtype, setEnqtype] = useState('')
  const [message, setMessage] = useState('')

  const [emailError, setEmailError] = useState(undefined)
  const [fnameError, setFnameError] = useState(undefined)
  const [messageError, setMessageError] = useState(undefined)
  const [enqtypeError, setEnqtypeError] = useState(undefined)
  const [phoneNumberError, setPhoneNumberError] = useState(undefined)
  const [formStatus, setFormStatus] = useState(null)

  const submitForm = () => {
    let error = false
    setEmailError(undefined)
    setFnameError(undefined)
    setEnqtypeError(undefined)
    setPhoneNumberError(undefined)
    setMessageError(undefined)

    if (!isEmail(email)) {
      setEmailError(['Please enter a valid email address'])
      error = true
    }
    if (isEmpty(fname)) {
      setFnameError(['Please enter your first name'])
      error = true
    }
    if (isEmpty(enqtype)) {
      setEnqtypeError(['Please select your enquiry type'])
      error = true
    }
    if (isEmpty(phoneNumber)) {
      setPhoneNumberError(['Please enter your phone number'])
      error = true
    }
    if (isEmpty(message)) {
      setMessageError(['Please enter your message'])
      error = true
    }

    if (!error) {
      setFormSubmitting.true()
      const params = new URLSearchParams()
      params.append('email', email)
      params.append('firstName', fname)
      params.append('enqtype', enqtype)
      params.append('phoneNumber', phoneNumber)
      params.append('message', message)

      axios
        .post('/api/contact', params)
        .then(({ data }) => {
          setFormSubmitting.false()
          setFormStatus(data.status)
        })
        .catch(err => {
          console.error(err)
        })
    }
  }

  const useStyles = createUseStyles(theme => ({
    container: {
      padding: '30px 15px',
      '@media (max-width: 700px)': {
        boxSizing: 'border-box',
        padding: '30px 15px',
      },
    },
    innerCont: { margin: '0 auto', width: '100%', maxWidth: '1220px' },
    title: {
      fontSize: '18px',
      lineHeight: '22px',
      fontWeight: 700,
      fontFamily: 'TTRounds',
      textAlign: 'left',
      marginBottom: '15px',
      marginTop: 0,
      color: theme.colors.main,
    },
    details: {
      fontSize: '18px',
      lineHeight: '26px',
      fontWeight: 400,
      textAlign: 'left',
      marginBottom: '30px',
      marginTop: 0,
      maxWidth: '560px',
      color: '#000',
      '@media (max-width: 600px)': {
        fontSize: '15px',
        lineHeight: 1.4,
      },
    },
    flexCont: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 20,
      '@media (max-width: 600px)': {
        gridTemplateColumns: '1fr',
        gap: 0,
      },
    },
    flexContFull: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: 20,
    },
    formsec: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      '@media (max-width: 600px)': {
        flexDirection: 'column',
      },
    },
    formarea: {
      width: '100%',
      maxWidth: 700,
    },
    tollarea: {
      width: 380,
      borderRadius: 20,
      backgroundColor: '#F5F5F5',
      padding: 40,
      boxSizing: 'border-box',
      '@media (max-width: 600px)': {
        width: '100%',
        marginTop: 60,
      },
    },
    tollareaTitle: {
      color: theme.colors.main,
      fontSize: 18,
      lineHeight: 1.3,
      marginBottom: 20,
    },
    tollareaNumber: {
      color: '#2D2D2D',
      fontSize: 15,
      lineHeight: 1.3,
      marginBottom: 10,
      '& a': {
        color: theme.colors.main,
      },
      '& span': {
        '&:nth-child(2)': {
          marginLeft: 3,
        },
      },
    },
    buttonPrimary: {
      display: 'inline-block',
      padding: '14px 0',
      border: 'none',
      color: '#fff',
      backgroundColor: theme.colors.orange,
      borderRadius: '15px',
      lineHeight: '18px',
      fontSize: '18px',
      fontWeight: 700,
      textDecoration: 'none',
      width: 215,
      '@media (max-width: 600px)': {
        display: 'block',
        width: '100%',
      },
      '&:hover': {
        backgroundColor: theme.colors.orangeHover,
        cursor: 'pointer',
      },
    },
  }))

  const classes = useStyles()

  return (
    <div className={classes.container}>
      <div className={classes.innerCont}>
        <div className={classes.formsec}>
          <div className={classes.formarea}>
            {body !== '' && <div className={classes.details}>{body}</div>}
            <div className={classes.title}>Email Enquiry</div>
            <div className={classes.form}>
              <div className={classes.flexCont}>
                <div>
                  <FormInput
                    greyBackground={true}
                    disabled={formSubmitting}
                    error={fnameError}
                    labelText="First Name*"
                    inputType="text"
                    defaultValue=""
                    onChange={el => {
                      setFname(el.currentTarget.value)
                    }}
                  />
                </div>
                <div>
                  <FormInput
                    disabled={formSubmitting}
                    greyBackground={true}
                    labelText="Email Address*"
                    inputType="email"
                    defaultValue=""
                    error={emailError}
                    onChange={el => {
                      setEmail(el.currentTarget.value)
                    }}
                  />
                </div>
              </div>
              <div className={classes.flexCont}>
                <div>
                  <FormSelect
                    greyBackground={true}
                    noSearch
                    disabled={formSubmitting}
                    defaultValue={enqtype}
                    onChange={e => {
                      setEnqtype(e.target.value)
                    }}
                    error={enqtypeError}
                    labelText="Enquiry Type"
                    optionsArray={['General', 'Sale'].map(type => ({
                      value: type,
                      label: type,
                    }))}
                    placeholder="Enquiry Type"
                  />
                </div>

                <div>
                  <FormInput
                    disabled={formSubmitting}
                    greyBackground={true}
                    labelText="Phone number*"
                    inputType="text"
                    defaultValue=""
                    error={phoneNumberError}
                    onChange={el => {
                      setPhoneNumber(el.currentTarget.value)
                    }}
                  />
                </div>
              </div>
              <div className={classes.flexContFull}>
                <FormTextArea
                  rows={4}
                  greyBackground
                  labelText="Message*"
                  placeholder="Message*"
                  inputName="message"
                  defaultValue={message}
                  error={messageError}
                  onChange={e => {
                    setMessage(e.target.value)
                  }}
                />
              </div>
              <div className={classes.flexContFull}>
                <div style={{ paddingTop: 20 }}>
                  <button onClick={submitForm} className={classes.buttonPrimary}>
                    Enquire Now
                  </button>
                </div>
              </div>

              {formStatus === true && (
                <div
                  style={{
                    border: '1px solid #F7F7F7',
                    boxShadow: '1px 5px 15px rgba(0, 0, 0, 0.15)',
                    borderRadius: 20,
                    padding: '50px 25px',
                    marginTop: 25,
                  }}
                >
                  <div style={{ fontSize: '20px', lineHeight: 1.3 }}>
                    Thank you for for your enquiry, we will be in touch with you shortly.
                  </div>
                  <div style={{ textAlign: 'center', paddingTop: 40 }}>
                    <div
                      style={{
                        backgroundColor: '#11BA22',
                        color: '#fff',
                        borderRadius: '18px',
                        padding: '18px 40px',
                        display: 'inline-flex',
                        alignItems: 'center',
                      }}
                    >
                      <SVG
                        name="TickCircle"
                        width={16}
                        height={16}
                        colorOne="#11BA22"
                        colorTwo="#fff"
                      />
                      <span style={{ display: 'inline-block', paddingLeft: 5 }}>Success</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className={classes.tollarea}>
            <div className={classes.tollareaTitle}>Toll-free Phone Numbers</div>
            {tollfree &&
              tollfree.length > 0 &&
              tollfree.map((phone, i) => (
                <div className={classes.tollareaNumber} key={`tollfree${i}`}>
                  <span>{phone.label}:</span>
                  <span>
                    <a href={`tel:${phone.number}`}>{phone.number}</a>
                  </span>
                </div>
              ))}
            <div className={classes.tollareaTitle} style={{ paddingTop: 30 }}>
              NOT Toll-free
            </div>
            {nottollfree &&
              nottollfree.length > 0 &&
              nottollfree.map((phone, i) => (
                <div className={classes.tollareaNumber} key={`nottollfree${i}`}>
                  <span>{phone.label}:</span>
                  <span>
                    <a href={`tel:${phone.number}`}>{phone.number}</a>
                  </span>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  )
}

Contactform.defaultProps = {}

export default Contactform
